/**
 * Excel Import Modal
 *
 * Full workflow for importing CSV or Excel files:
 *   1. Upload
 *   2. Sheet selection (multi-sheet files only)
 *   3. Data Prep  — auto-detects and asks about data-quality issues
 *   4. Map Columns — maps CSV headers to app fields, supports inline field creation
 *   5. Preview     — row-level validation summary
 *   6. Confirm     — fires onImport with cleaned, structured rows
 *
 * Custom field values are restructured into items.metadata before onImport is
 * called, so callers receive standard rows regardless of whether custom fields
 * were used.
 */

import { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import {
  X,
  FileSpreadsheet,
  ChevronRight,
  ChevronLeft,
  Check,
  AlertCircle,
  Loader2,
} from 'lucide-react';
import type { Attribute } from '../../../types/prioritization';
import { importFromExcel, applyColumnMapping, applyDataPrepConfig, buildDefaultDataPrepConfig, type ExcelImportResult, type DataPrepConfig } from '../../../lib/excelService';
import { ColumnMapper, type AppField, type ColumnMappingState } from './ColumnMapper';
import { ImportPreview, type PreviewRow } from './ImportPreview';
import { DataPrepStep } from './DataPrepStep';
import { restructureCustomFieldRows } from '../../../lib/importConfigs';
import { useAttributesStore } from '../../../stores/prioritization/attributesStore';
import { getCurrentWorkspaceId } from '../../../lib/supabaseSync';
import {
  listCsvImportProfiles,
  loadCsvImportProfileMappings,
  saveCsvImportProfile,
} from '../../../lib/dataCitizenship/csvMappingProfiles';
import type { FieldMappingSet } from '../../../lib/fieldMappingRegistry';

// ============================================================================
// Types
// ============================================================================

export interface ExcelImportMeta {
  /** Named Field Mapping set saved / selected for this run (connector csv). */
  profileName?: string;
  profileId?: string;
}

export interface ExcelImportModalProps {
  isOpen: boolean;
  onClose: () => void;
  onImport: (data: Record<string, unknown>[], meta?: ExcelImportMeta) => void | Promise<void>;
  /** Immediately process a file dropped onto the parent page. */
  initialFile?: File | null;
  /** Static field definitions from importConfigs; custom fields are loaded automatically */
  appFields: AppField[];
  title?: string;
  description?: string;
  validateRow?: (row: Record<string, unknown>) => { isValid: boolean; errors: string[]; warnings: string[] };
  /**
   * When true, workspace custom fields are loaded and merged into the column
   * mapper. Pass false if the import is not for items (default: true).
   */
  enableCustomFields?: boolean;
  /**
   * Persist Map Columns as a named CSV Field Mapping set (Settings → Field Mappings).
   * Default true for item imports.
   */
  enableCitizenshipProfiles?: boolean;
}

type ImportStep = 'upload' | 'sheet' | 'dataprep' | 'mapping' | 'preview' | 'importing';

// Slug helper: "Business Impact" → "business_impact"
function slugify(s: string): string {
  return s
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_|_$/g, '');
}

// ============================================================================
// Component
// ============================================================================

export function ExcelImportModal({
  isOpen,
  onClose,
  onImport,
  appFields,
  title = 'Import from File',
  description = 'Upload a CSV or Excel file to import data',
  validateRow,
  enableCustomFields = true,
  enableCitizenshipProfiles = true,
  initialFile,
}: ExcelImportModalProps) {
  // ── Store ─────────────────────────────────────────────────────────────────
  const addAttribute = useAttributesStore(s => s.addAttribute);
  const getEnabledCustomFields = useAttributesStore(s => s.getEnabledCustomFields);

  // ── State ─────────────────────────────────────────────────────────────────
  const [step, setStep] = useState<ImportStep>('upload');
  const [file, setFile] = useState<File | null>(null);
  const [importResult, setImportResult] = useState<ExcelImportResult | null>(null);
  const [selectedSheet, setSelectedSheet] = useState<string>('');
  const [dataPrepConfig, setDataPrepConfig] = useState<DataPrepConfig | null>(null);
  const [columnMappings, setColumnMappings] = useState<ColumnMappingState>({});
  const [dynamicCustomFields, setDynamicCustomFields] = useState<AppField[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isDragOver, setIsDragOver] = useState(false);
  const processedInitialFileRef = useRef<File | null>(null);
  const [profiles, setProfiles] = useState<FieldMappingSet[]>([]);
  const [selectedProfileId, setSelectedProfileId] = useState<string>('');
  const [profileNameDraft, setProfileNameDraft] = useState('');
  const [profileBusy, setProfileBusy] = useState(false);
  const [profileMessage, setProfileMessage] = useState<string | null>(null);
  const [mapperKey, setMapperKey] = useState(0);

  // ── Derived ───────────────────────────────────────────────────────────────

  // All app fields: static + workspace custom + inline-created this session
  const allAppFields = useMemo<AppField[]>(() => {
    if (!enableCustomFields) return appFields;

    const workspaceCustom = getEnabledCustomFields('item').map(attr => ({
      key: attr.key,
      label: attr.label,
      required: false,
      type: (attr.type === 'number' ? 'number' :
             attr.type === 'boolean' ? 'boolean' :
             attr.type === 'date' ? 'date' : 'string') as AppField['type'],
      isCustomField: true as const,
      description: attr.description,
    }));

    // Merge workspace custom + session-created, dedup by key
    const seen = new Set<string>();
    const merged: AppField[] = [];
    for (const f of [...workspaceCustom, ...dynamicCustomFields]) {
      if (!seen.has(f.key)) { seen.add(f.key); merged.push(f); }
    }

    return [...appFields, ...merged];
  }, [appFields, enableCustomFields, getEnabledCustomFields, dynamicCustomFields]);

  // Data-prep runs on raw parsed rows (before any config is applied)
  const currentSheetData = useMemo(() => {
    if (!importResult || !selectedSheet) return null;
    return importResult.sheets.find(s => s.name === selectedSheet) ?? null;
  }, [importResult, selectedSheet]);

  // Rows after data prep transformation
  const preparedRows = useMemo(() => {
    if (!currentSheetData || !dataPrepConfig) return currentSheetData?.data ?? [];
    return applyDataPrepConfig(currentSheetData.data, dataPrepConfig, currentSheetData.headers);
  }, [currentSheetData, dataPrepConfig]);

  // Preview rows with mapping + validation applied
  const previewRows = useMemo<PreviewRow[]>(() => {
    if (!preparedRows.length) return [];

    const mappings = Object.entries(columnMappings)
      .filter(([, v]) => v !== null)
      .map(([excelColumn, appField]) => ({ excelColumn, appField: appField as string }));

    const mappedData = applyColumnMapping(preparedRows, mappings);

    return mappedData.map(data => {
      if (validateRow) {
        const v = validateRow(data);
        return { data, ...v };
      }

      const errors: string[] = [];
      for (const field of allAppFields) {
        if (field.required && !data[field.key]) {
          errors.push(`Missing required field: ${field.label}`);
        }
      }
      return { data, isValid: errors.length === 0, errors, warnings: [] };
    });
  }, [preparedRows, columnMappings, allAppFields, validateRow]);

  // Custom field keys (for restructuring before onImport)
  const customFieldKeys = useMemo(
    () => new Set(allAppFields.filter(f => f.isCustomField).map(f => f.key)),
    [allAppFields]
  );

  // ── Handlers ──────────────────────────────────────────────────────────────

  const processFile = useCallback(async (selectedFile: File) => {
    setError(null);
    setIsProcessing(true);
    setFile(selectedFile);

    try {
      const result = await importFromExcel(selectedFile);

      if (!result.success) {
        setError(result.error || 'Failed to parse file');
        return;
      }
      if (result.sheets.length === 0) {
        setError('No sheets found in file');
        return;
      }

      setImportResult(result);

      if (result.sheets.length === 1) {
        const sheet = result.sheets[0];
        setSelectedSheet(sheet.name);
        const config = buildDefaultDataPrepConfig(sheet.headers, sheet.data);
        setDataPrepConfig(config);
        setStep('dataprep');
      } else {
        setStep('sheet');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to process file');
    } finally {
      setIsProcessing(false);
    }
  }, []);

  useEffect(() => {
    if (!isOpen || !initialFile || processedInitialFileRef.current === initialFile) return;
    processedInitialFileRef.current = initialFile;
    void processFile(initialFile);
  }, [initialFile, isOpen, processFile]);

  const handleFileChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (!selectedFile) return;
    processFile(selectedFile);
  }, [processFile]);

  const handleDrop = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragOver(false);
    if (isProcessing) return;
    const dropped = e.dataTransfer.files?.[0];
    if (!dropped) return;
    const ext = dropped.name.split('.').pop()?.toLowerCase();
    if (!ext || !['csv', 'xlsx', 'xls'].includes(ext)) {
      setError('Please drop a CSV or Excel file (.csv, .xlsx, .xls)');
      return;
    }
    processFile(dropped);
  }, [isProcessing, processFile]);

  const handleSheetSelect = useCallback((sheetName: string) => {
    const sheet = importResult?.sheets.find(s => s.name === sheetName);
    if (!sheet) return;

    setSelectedSheet(sheetName);
    setColumnMappings({});

    const config = buildDefaultDataPrepConfig(sheet.headers, sheet.data);
    setDataPrepConfig(config);
    setStep('dataprep');
  }, [importResult]);

  const handleDataPrepContinue = useCallback(() => {
    if (!dataPrepConfig || !currentSheetData) return;

    // If data prep auto-creates boolean fields, schedule them
    // (auto-created fields are handled in the mapping step via dynamicCustomFields)
    // Proceed to mapping
    setColumnMappings({});
    setStep('mapping');
  }, [dataPrepConfig, currentSheetData]);

  const handleMappingChange = useCallback((m: ColumnMappingState) => {
    setColumnMappings(m);
  }, []);

  const handleCreateField = useCallback(async (
    _forColumn: string,
    draft: { label: string; type: NonNullable<AppField['type']> }
  ): Promise<AppField> => {
    const key = slugify(draft.label);

    const attrType: Attribute['type'] =
      draft.type === 'boolean' ? 'boolean' :
      draft.type === 'number'  ? 'number'  :
      draft.type === 'date'    ? 'date'    :
      'text';

    const attr: Attribute = {
      key,
      label: draft.label,
      type: attrType,
      required: false,
      isCustomField: true,
      enabled: true,
      objectType: 'item',
      scope: 'global',
      inheritable: true,
      overridable: true,
    };

    const { error: storeErr } = await addAttribute(attr);
    if (storeErr) throw new Error(storeErr);

    const newField: AppField = {
      key,
      label: draft.label,
      required: false,
      type: draft.type,
      isCustomField: true,
    };

    setDynamicCustomFields(prev => {
      if (prev.some(f => f.key === key)) return prev;
      return [...prev, newField];
    });

    return newField;
  }, [addAttribute]);

  const handleProceedToPreview = useCallback(() => {
    const mapped = new Set(Object.values(columnMappings).filter(Boolean));
    const missing = allAppFields.filter(f => f.required && !mapped.has(f.key));

    if (missing.length > 0) {
      setError(`Missing required fields: ${missing.map(f => f.label).join(', ')}`);
      return;
    }

    setError(null);
    setStep('preview');
  }, [columnMappings, allAppFields]);

  const resetState = useCallback(() => {
    setStep('upload');
    setFile(null);
    setImportResult(null);
    setSelectedSheet('');
    setDataPrepConfig(null);
    setColumnMappings({});
    setDynamicCustomFields([]);
    setError(null);
    setIsProcessing(false);
    setIsDragOver(false);
    setSelectedProfileId('');
    setProfileNameDraft('');
    setProfileMessage(null);
  }, []);

  const handleClose = useCallback(() => { onClose(); resetState(); }, [onClose, resetState]);

  useEffect(() => {
    if (!isOpen || !enableCitizenshipProfiles) return;
    const workspaceId = getCurrentWorkspaceId();
    if (!workspaceId) return;
    void listCsvImportProfiles(workspaceId).then((sets) => {
      setProfiles(sets);
    });
  }, [isOpen, enableCitizenshipProfiles]);

  const handleLoadProfile = useCallback(async (setId: string) => {
    setSelectedProfileId(setId);
    setProfileMessage(null);
    if (!setId) return;
    const workspaceId = getCurrentWorkspaceId();
    if (!workspaceId) return;
    const { set, mappings } = await loadCsvImportProfileMappings(workspaceId, setId);
    if (set) {
      setColumnMappings((prev) => {
        // Keep headers from current file; apply saved header→field where headers match
        const next: ColumnMappingState = { ...prev };
        for (const [header, field] of Object.entries(mappings)) {
          if (header in next || Object.keys(next).length === 0) {
            next[header] = field;
          }
        }
        // If mapper hasn't initialized headers yet, use saved mappings wholesale
        if (Object.keys(prev).length === 0) return mappings;
        for (const [header, field] of Object.entries(mappings)) {
          if (header in prev) next[header] = field;
        }
        return next;
      });
      setProfileNameDraft(set.name);
      setProfileMessage(`Loaded profile “${set.name}”`);
      setMapperKey((k) => k + 1);
    }
  }, []);

  const handleSaveProfile = useCallback(async () => {
    const workspaceId = getCurrentWorkspaceId();
    if (!workspaceId) {
      setProfileMessage('No workspace — cannot save profile');
      return;
    }
    const name = profileNameDraft.trim();
    if (!name) {
      setProfileMessage('Enter a profile name to save');
      return;
    }
    setProfileBusy(true);
    const { set, error: saveErr } = await saveCsvImportProfile({
      workspaceId,
      name,
      mappings: columnMappings,
      setId: selectedProfileId || undefined,
    });
    setProfileBusy(false);
    if (saveErr) {
      setProfileMessage(saveErr);
      return;
    }
    setSelectedProfileId(set.id);
    setProfiles((prev) => {
      const others = prev.filter((p) => p.id !== set.id);
      return [...others, set].sort((a, b) => a.name.localeCompare(b.name));
    });
    setProfileMessage(`Saved profile “${set.name}” (Settings → Field Mappings → CSV)`);
  }, [profileNameDraft, columnMappings, selectedProfileId]);

  const handleImport = useCallback(async () => {
    const validRows = previewRows.filter(r => r.isValid).map(r => r.data);
    if (validRows.length === 0) { setError('No valid rows to import'); return; }

    // Restructure custom field values into metadata
    const finalRows = restructureCustomFieldRows(validRows, customFieldKeys);

    // Handle section column in 'tag' mode
    if (dataPrepConfig?.sectionColumn && dataPrepConfig.sectionColumnTreatment === 'tag') {
      finalRows.forEach(row => {
        if (row['__section_tag__']) {
          const existing = (row.tags as string[] | undefined) ?? [];
          const extra = String(row['__section_tag__']).split(',').map(s => s.trim()).filter(Boolean);
          row.tags = [...new Set([...existing, ...extra])];
          delete row['__section_tag__'];
        }
      });
    }

    // Persist mapping profile when a name is present (idempotent re-runs)
    let profileMeta: ExcelImportMeta | undefined;
    if (enableCitizenshipProfiles && profileNameDraft.trim()) {
      const workspaceId = getCurrentWorkspaceId();
      if (workspaceId) {
        const { set } = await saveCsvImportProfile({
          workspaceId,
          name: profileNameDraft.trim(),
          mappings: columnMappings,
          setId: selectedProfileId || undefined,
        });
        profileMeta = { profileName: set.name, profileId: set.id };
      } else {
        profileMeta = { profileName: profileNameDraft.trim(), profileId: selectedProfileId || undefined };
      }
    } else if (selectedProfileId) {
      const selected = profiles.find((p) => p.id === selectedProfileId);
      profileMeta = { profileName: selected?.name, profileId: selectedProfileId };
    }

    setStep('importing');
    try {
      await onImport(finalRows, profileMeta);
    } finally {
      onClose();
      resetState();
    }
  }, [
    previewRows,
    customFieldKeys,
    dataPrepConfig,
    onImport,
    onClose,
    resetState,
    enableCitizenshipProfiles,
    profileNameDraft,
    columnMappings,
    selectedProfileId,
    profiles,
  ]);

  const handleBack = useCallback(() => {
    switch (step) {
      case 'sheet':    resetState(); break;
      case 'dataprep':
        if (importResult && importResult.sheets.length > 1) setStep('sheet');
        else resetState();
        break;
      case 'mapping':  setStep('dataprep'); break;
      case 'preview':  setStep('mapping'); break;
    }
  }, [step, importResult, resetState]);

  if (!isOpen) return null;

  const validRowCount  = previewRows.filter(r => r.isValid).length;
  const mappedColumns  = Object.values(columnMappings).filter(Boolean).length;
  const totalPrepared  = preparedRows.length;
  const rawTotal       = currentSheetData?.data.length ?? 0;
  const filteredCount  = rawTotal - totalPrepared;

  // Step number helpers (data prep is step 2 only when it has issues, but we
  // always show it for clarity)
  const STEPS: { key: ImportStep; label: string }[] = [
    { key: 'upload',   label: 'Upload' },
    { key: 'dataprep', label: 'Data Prep' },
    { key: 'mapping',  label: 'Map Columns' },
    { key: 'preview',  label: 'Preview' },
  ];
  const stepIndex = (s: ImportStep) => STEPS.findIndex(x => x.key === s);
  const currentIdx = stepIndex(step);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
      <div className="bg-[var(--bg-raised)] shadow-xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">

        {/* Header */}
        <div className="px-6 py-4 border-b border-[var(--rule)] flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold text-[var(--text-pri)]">{title}</h2>
            <p className="text-sm text-[var(--text-ter)]">{description}</p>
          </div>
          <button onClick={handleClose} className="p-2 text-[var(--text-ter)] hover:text-[var(--text-sec)] hover:bg-[var(--hover-tint)]">
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Progress */}
        {step !== 'sheet' && (
          <div className="px-6 py-3 border-b border-[var(--rule)] bg-[var(--surface)]">
            <div className="flex items-center gap-1.5 text-sm">
              {STEPS.map((s, i) => (
                <span key={s.key} className="flex items-center gap-1.5">
                  {i > 0 && <ChevronRight className="h-3.5 w-3.5 text-[var(--text-ter)]" />}
                  <StepIndicator
                    number={i + 1}
                    label={s.label}
                    isActive={step === s.key || (step === 'importing' && s.key === 'preview')}
                    isComplete={currentIdx > i || step === 'importing'}
                  />
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {error && (
            <div className="mb-4 p-3 bg-[var(--coral-dim)] border border-[var(--coral)] flex items-start gap-2">
              <AlertCircle className="h-5 w-5 text-[var(--coral)] flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-[var(--coral)]">Error</p>
                <p className="text-sm text-[var(--coral)]">{error}</p>
              </div>
            </div>
          )}

          {/* Upload */}
          {step === 'upload' && (
            <div className="flex flex-col items-center justify-center py-12">
              <div
                onDrop={handleDrop}
                onDragOver={e => { e.preventDefault(); setIsDragOver(true); }}
                onDragLeave={() => setIsDragOver(false)}
                className="w-full max-w-md"
              >
                <label className="cursor-pointer block">
                  <div className={`flex flex-col items-center gap-4 p-8 border-2 border-dashed  transition-colors ${
                    isDragOver
                      ? 'border-primary-500 bg-primary-50'
                      : 'border-[var(--rule)] hover:border-primary-400 hover:bg-primary-50'
                  }`}>
                    {isProcessing ? (
                      <Loader2 className="h-12 w-12 text-primary-500 animate-spin" />
                    ) : (
                      <FileSpreadsheet className={`h-12 w-12 ${isDragOver ? 'text-primary-500' : 'text-[var(--text-ter)]'}`} />
                    )}
                    <div className="text-center">
                      <p className="text-lg font-medium text-[var(--text-pri)]">
                        {isProcessing ? 'Parsing file…' : isDragOver ? 'Drop to import' : 'Drop file here or click to browse'}
                      </p>
                      <p className="text-sm text-[var(--text-ter)] mt-1">Supports CSV, .xlsx and .xls</p>
                    </div>
                  </div>
                  <input
                    type="file"
                    accept=".csv,.xlsx,.xls"
                    onChange={handleFileChange}
                    className="hidden"
                    disabled={isProcessing}
                  />
                </label>
              </div>
              {file && !isProcessing && (
                <p className="mt-4 text-sm text-[var(--text-ter)]">Selected: {file.name}</p>
              )}
            </div>
          )}

          {/* Sheet selection */}
          {step === 'sheet' && importResult && (
            <div className="space-y-4">
              <p className="text-sm text-[var(--text-sec)]">This file contains multiple sheets. Select one to import:</p>
              <div className="grid gap-3">
                {importResult.sheets.map(sheet => (
                  <button
                    key={sheet.name}
                    onClick={() => handleSheetSelect(sheet.name)}
                    className="flex items-center justify-between p-4 border border-[var(--rule)] hover:border-primary-400 hover:bg-primary-50 transition-colors text-left"
                  >
                    <div>
                      <p className="font-medium text-[var(--text-pri)]">{sheet.name}</p>
                      <p className="text-sm text-[var(--text-ter)]">{sheet.data.length} rows · {sheet.headers.length} columns</p>
                    </div>
                    <ChevronRight className="h-5 w-5 text-[var(--text-ter)]" />
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Data Prep */}
          {step === 'dataprep' && currentSheetData && dataPrepConfig && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-[var(--text-pri)]">
                    {rawTotal} rows detected
                    {filteredCount > 0 && ` · ${filteredCount} will be skipped`}
                  </p>
                  <p className="text-sm text-[var(--text-ter)]">{currentSheetData.headers.length} columns</p>
                </div>
              </div>
              <DataPrepStep
                headers={currentSheetData.headers}
                rows={currentSheetData.data}
                config={dataPrepConfig}
                onChange={setDataPrepConfig}
              />
            </div>
          )}

          {/* Column Mapping */}
          {step === 'mapping' && currentSheetData && (
            <div className="space-y-4">
              {enableCitizenshipProfiles && (
                <div className="border border-[var(--rule)] bg-[var(--surface)] p-4 space-y-3">
                  <div>
                    <p className="text-sm font-medium text-[var(--text-pri)]">Import profile</p>
                    <p className="text-sm text-[var(--text-ter)]">
                      Save column mappings to Settings → Field Mappings → CSV. Re-runs upsert by External ID.
                    </p>
                  </div>
                  <div className="flex flex-col sm:flex-row gap-3">
                    <label className="flex-1 text-sm text-[var(--text-sec)]">
                      <span className="block mb-1">Load saved profile</span>
                      <select
                        className="w-full border border-[var(--rule)] bg-[var(--bg-raised)] px-3 py-2 text-sm"
                        value={selectedProfileId}
                        onChange={(e) => void handleLoadProfile(e.target.value)}
                      >
                        <option value="">— None —</option>
                        {profiles.map((p) => (
                          <option key={p.id} value={p.id}>{p.name}</option>
                        ))}
                      </select>
                    </label>
                    <label className="flex-1 text-sm text-[var(--text-sec)]">
                      <span className="block mb-1">Profile name</span>
                      <input
                        type="text"
                        className="w-full border border-[var(--rule)] bg-[var(--bg-raised)] px-3 py-2 text-sm"
                        placeholder="e.g. Weekly product backlog"
                        value={profileNameDraft}
                        onChange={(e) => setProfileNameDraft(e.target.value)}
                      />
                    </label>
                    <div className="flex items-end">
                      <button
                        type="button"
                        onClick={() => void handleSaveProfile()}
                        disabled={profileBusy}
                        className="btn btn-secondary whitespace-nowrap disabled:opacity-50"
                      >
                        {profileBusy ? 'Saving…' : 'Save profile'}
                      </button>
                    </div>
                  </div>
                  {profileMessage && (
                    <p className="text-sm text-[var(--text-sec)]">{profileMessage}</p>
                  )}
                </div>
              )}
              <ColumnMapper
                key={`mapper-${mapperKey}`}
                excelHeaders={currentSheetData.headers}
                appFields={allAppFields}
                initialMappings={columnMappings}
                onChange={handleMappingChange}
                onCreateField={enableCustomFields ? handleCreateField : undefined}
              />
            </div>
          )}

          {/* Preview */}
          {step === 'preview' && (
            <ImportPreview
              rows={previewRows}
              columns={allAppFields.map(f => ({ key: f.key, label: f.label }))}
            />
          )}

          {/* Importing */}
          {step === 'importing' && (
            <div className="flex flex-col items-center justify-center py-12">
              <Loader2 className="h-12 w-12 text-primary-500 animate-spin mb-4" />
              <p className="text-lg font-medium text-[var(--text-pri)]">Importing…</p>
              <p className="text-sm text-[var(--text-ter)]">Importing {validRowCount} items</p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-[var(--rule)] flex items-center justify-between bg-[var(--surface)]">
          <div className="text-sm text-[var(--text-ter)]">
            {step === 'dataprep' && filteredCount > 0 && `${filteredCount} rows will be filtered`}
            {step === 'mapping' && `${mappedColumns} columns mapped`}
            {step === 'preview' && `${validRowCount} of ${previewRows.length} rows valid`}
          </div>

          <div className="flex items-center gap-3">
            {step !== 'upload' && step !== 'importing' && (
              <button onClick={handleBack} className="px-4 py-2 text-sm text-[var(--text-sec)] hover:text-[var(--text-pri)] flex items-center gap-1">
                <ChevronLeft className="h-4 w-4" />
                Back
              </button>
            )}

            {step === 'dataprep' && (
              <button onClick={handleDataPrepContinue} className="btn btn-primary">
                Continue to Mapping
              </button>
            )}

            {step === 'mapping' && (
              <button onClick={handleProceedToPreview} className="btn btn-primary">
                Preview Import
              </button>
            )}

            {step === 'preview' && (
              <button
                onClick={handleImport}
                disabled={validRowCount === 0}
                className="btn btn-primary disabled:opacity-50 flex items-center gap-2"
              >
                <Check className="h-4 w-4" />
                Import {validRowCount} Item{validRowCount !== 1 ? 's' : ''}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Step Indicator ────────────────────────────────────────────────────────────

function StepIndicator({
  number,
  label,
  isActive,
  isComplete,
}: {
  number: number;
  label: string;
  isActive: boolean;
  isComplete: boolean;
}) {
  return (
    <div className={`flex items-center gap-1.5 ${isActive ? 'text-primary-600' : isComplete ? 'text-[var(--green)] dark:text-[var(--text-ter)]' : 'text-[var(--text-ter)]'}`}>
      <div className={`flex items-center justify-center w-5 h-5 rounded-full text-xs font-medium ${
        isActive   ? 'bg-primary-100 text-primary-700' :
        isComplete ? 'bg-[var(--green-dim)] text-[var(--green)] dark:text-[var(--text-ter)]' :
                     'bg-[var(--hover-tint)] text-[var(--text-ter)]'
      }`}>
        {isComplete ? <Check className="h-3 w-3" /> : number}
      </div>
      <span className="font-medium text-sm">{label}</span>
    </div>
  );
}

export default ExcelImportModal;
